import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  Tooltip,
  MenuItem,
  InputLabel,
  FormControl,
  Select,
  InputAdornment,
  Stepper,
  Step,
  StepLabel,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Formik } from "formik";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import * as yup from "yup";
import Swal from "sweetalert2";
import {
  executeDynamicSubmit,
  executeAnyMutation,
  fetchJsonDynamic,
} from "../../../data/DynamicServiceNew/jsondynamicservice";
import Header from "../../../components/Header";
import { setupManagementUrl, loanManagementUrl } from "../../../config";
import { useEffect, useState } from "react";
import { allInterestPaymentMethods } from "../../../data/Axios/queries";
import { NumericFormat } from "react-number-format";
import { logAuditChange, computeFieldDiff } from "../../../components/AuditTrail/auditLogger";

const setup_url = setupManagementUrl.uri;

const STEPS = [
  {
    label: "Basic Details",
    description: "Name the product and set its interest configuration.",
  },
  {
    label: "Installments & Amounts",
    description: "Define the repayment schedule and the loan amount range.",
  },
  {
    label: "Collateral & Penalties",
    description:
      "Configure collateral requirements and overdue-installment handling.",
  },
  {
    label: "Incentives & Extras",
    description: "Optional incentives and other product behaviors.",
  },
];

// Which Formik fields belong to a step — only the fields that are actually
// relevant given the current values (e.g. skip minimumTerm/maximumTerm when
// the product doesn't use dynamic installments) are checked before Next.
const getStepFields = (step, values) => {
  switch (step) {
    case 0:
      return [
        "name",
        "description",
        "productInterest",
        "interestMethodId",
        "interestPaymentMethodId",
        "minCommission",
        "maxCommission",
        "repaymentFrequencyId",
      ];
    case 1:
      return [
        ...(values.isdynamicInstallment === 1
          ? ["minimumTerm", "maximumTerm"]
          : ["noOfInstallments"]),
        "minimumAmount",
        "maximumAmount",
        ...(values.isDepositRequired ? ["depositRequired"] : []),
      ];
    case 2:
      return [
        ...(values.isSecuredproduct
          ? ["maxLtvPct", "loanTermGracePeriod"]
          : []),
        ...(values.hasPenalty || values.allowInstallmentRepossesion
          ? ["lienGracePeriod"]
          : []),
        ...(values.hasPenalty ? ["installmentPenalty"] : []),
      ];
    case 3:
      return values.hasIncentive ? ["incentiveValue", "minimumLoanPaid"] : [];
    default:
      return [];
  }
};

// A small "label + info tooltip" wrapper so every checkbox row looks the same
// without repeating the Tooltip/IconButton boilerplate everywhere.
const CheckboxField = ({ checked, onChange, name, label, tooltip }) => (
  <FormControlLabel
    control={<Checkbox checked={checked} onChange={onChange} name={name} />}
    label={
      <Box display="flex" alignItems="center" gap={0.5}>
        {label}
        {tooltip && (
          <Tooltip title={tooltip}>
            <InfoOutlinedIcon
              fontSize="small"
              sx={{ color: "action.active", cursor: "help" }}
            />
          </Tooltip>
        )}
      </Box>
    }
  />
);

const infoAdornment = (tooltip) => ({
  endAdornment: (
    <InputAdornment position="end">
      <Tooltip title={tooltip}>
        <InfoOutlinedIcon
          fontSize="small"
          sx={{ color: "action.active", cursor: "help" }}
        />
      </Tooltip>
    </InputAdornment>
  ),
});

// Shared NumericFormat props so every "amount / percentage" field behaves
// identically (correct cursor position while typing, live thousands
// separators, no forced decimal padding mid-keystroke).
const numericFieldProps = (tooltip) => ({
  InputProps: {
    endAdornment: (
      <InputAdornment position="end">
        <Tooltip title={tooltip}>
          <InfoOutlinedIcon
            fontSize="small"
            sx={{ color: "action.active", cursor: "help" }}
          />
        </Tooltip>
      </InputAdornment>
    ),
  },
});

const Form = (props) => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const CompanyId = decodedToken.CompanyId;
  const [interestPaymentMethods, setInterestPaymentMethods] = useState([]);
  const [interestMethods, setInterestMethods] = useState([]);
  const [isDynamicInstallment, setIsDynamicInstallment] = useState(
    props.data ? props.data.IsdynamicInstallment : 0,
  );
  const [repaymentFrequencys, setRepaymentFrequencys] = useState([]);
  const [activeStep, setActiveStep] = useState(0);
  const [isAssetFinanceEnabled, setIsAssetFinanceEnabled] = useState(false);
  // Separate loan setting ("Is Device") that controls only the
  // "Is Product Device" checkbox, independent of the asset finance setting.
  const [isDeviceEnabled, setIsDeviceEnabled] = useState(false);
  const [isDeviceCustomerChecked, setIsDeviceDeviceChecked] =
    useState(false);
    
  // ── Fetch loan settings ───────────────────────────────────────────────────
  useEffect(() => {
    async function fetchLoanSettings() {
      try {
        const response = await fetchJsonDynamic(
          "loanSettings",
          null,
          null,
          loanManagementUrl.uri,
        );
        if (response?.results) {
          const settings = JSON.parse(response.results);
          const assetFinanceSetting = settings.find(
            (s) => s.Setting === "Is Asset Finance",
          );
          setIsAssetFinanceEnabled(
            assetFinanceSetting?.Value?.toLowerCase() === "true",
          );

          const deviceSetting = settings.find(
            (s) => s.Setting === "Is Device",
          );
          setIsDeviceEnabled(deviceSetting?.Value?.toLowerCase() === "true");
        }
      } catch (error) {
        console.error("Error fetching loan settings:", error);
        setIsAssetFinanceEnabled(false);
        setIsDeviceEnabled(false);
      }
    }
    fetchLoanSettings();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      const formattedToken = `Bearer ${token?.replace(/"/g, "")}`;
      try {
        const interestpaymentResponse = await axios.post(
          setup_url,
          allInterestPaymentMethods,
          {
            headers: {
              Authorization: formattedToken,
              "Content-Type": "application/json",
            },
          },
        );

        const interestPaymentresponse =
          interestpaymentResponse?.data?.data?.interestPaymentMethods?.results;

        const interestMethodResponse = await fetchJsonDynamic(
          "interestMethods",
          null,
          null,
          setup_url,
        );
        const InterestMethodResponse = interestMethodResponse?.results;

        const repaymentFrequencyResponse = await fetchJsonDynamic(
          "productRepaymentFrequency",
          null,
          null,
          setup_url,
        );
        const repaymentfrequencyResponse = repaymentFrequencyResponse?.results;

        if (InterestMethodResponse) {
          const interstmethods = JSON.parse(InterestMethodResponse);
          setInterestMethods(interstmethods || []);
        }
        if (repaymentfrequencyResponse) {
          const frequencys = JSON.parse(repaymentfrequencyResponse);
          setRepaymentFrequencys(frequencys || []);
        }
        if (interestPaymentresponse) {
          const interestData = JSON.parse(interestPaymentresponse);
          setInterestPaymentMethods(interestData);
        } else {
          console.error("Results field is not found or is empty.");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const initialValues = {
    name: props.data ? props.data.Name : "",
    description: props.data ? props.data.Description : "",
    noOfInstallments: props.data ? props.data.NoOfInstallments : "",
    productInterest: props.data ? props.data.ProductInterest : "",
    isDepositRequired: props.data ? !!props.data.IsDepositRequired : false,
    depositRequired: props.data ? props.data.DepositRequired : "",
    isGroup: props.data ? !!props.data.IsGroup : false,
    isDepositPercentage: props.data ? !!props.data.IsDepositPercentage : false,
    minimumAmount: props.data ? props.data.MinimumAmount : 0,
    maximumAmount: props.data ? props.data.MaximumAmount : "",
    interestMethodId: props.data ? props.data.InterestMethodId || "" : "",
    interestPaymentMethodId: props.data
      ? props.data.InterestPaymentMethodId || ""
      : "",
    repaymentFrequencyId: props.data
      ? props.data.RepaymentFrequencyId || ""
      : "",
    isdynamicInstallment: props.data ? props.data.IsdynamicInstallment || 0 : 0,
    minimumTerm: props.data ? props.data.MinimumTerm || "" : "",
    maximumTerm: props.data ? props.data.maximumTerm || "" : "",
    isSecuredproduct: props.data ? !!props.data.IsSecuredproduct : false,
    maxLtvPct: props.data ? props.data.MaxLtvPct || "" : "",
    lienGracePeriod: props.data ? props.data.LienGracePeriod || "" : "",
    installmentPenalty: props.data ? (props.data.InstallmentPenalty ?? "") : "",
    loanTermGracePeriod: props.data
      ? (props.data.LoanTermGracePeriod ?? "")
      : "",
    armotizeSchedule: props.data ? !!props.data.ArmotizeSchedule : false,
    isBlankDisbursement: props.data ? !!props.data.IsBlankDisbursement : false,
    hasPenalty: props.data ? !!props.data.HasPenalty : false,
    allowInstallmentRepossesion: props.data
      ? !!props.data.AllowInstallmentRepossesion
      : false,
    hasIncentive: props.data ? !!props.data.HasIncentive : false,
    isIncentivePercentage: props.data
      ? !!props.data.IsIncentivePercentage
      : false,
    incentiveValue: props.data ? (props.data.IncentiveValue ?? "") : "",
    minimumLoanPaid: props.data ? (props.data.MinimumLoanPaid ?? "") : "",
    minCommission: props.data ? (props.data.MinCommission ?? "") : "",
    maxCommission: props.data ? (props.data.MaxCommission ?? "") : "",
    isDeviceCustomer: props.data ? !!props.data.IsDeviceCustomer : false,

  };

  const validationSchema = yup.object().shape({
    name: yup.string().required("Name required"),
    description: yup.string().required("Description required"),
    productInterest: yup
      .number()
      .typeError("Product Interest must be a valid number")
      .required("Product Interest required")
      .min(0, "Product Interest must be at least 0%")
      .max(100, "Product Interest must be below 100%"),
    depositRequired: yup.number().when("isDepositRequired", {
      is: (isDepositRequired) => isDepositRequired,
      then: yup
        .number()
        .required("Deposit Required")
        .typeError("Deposit must be a valid number")
        .max(100, "Deposit must be below 100%"),
      otherwise: yup.number().nullable(true),
    }),
    interestMethodId: yup
      .string()
      .required("Interest Method selection required"),
    noOfInstallments: yup.number().when("isDynamicInstallment", {
      is: 0,
      then: yup.number().required("No Of Installments required"),
      otherwise: yup.number().nullable(true),
    }),
    minimumTerm: yup.number().when("isdynamicInstallment", {
      is: 1,
      then: yup
        .number()
        .required("Minimum Installments required")
        .min(1, "Minimum must be at least 1"),
      otherwise: yup.number().nullable(true),
    }),
    maximumTerm: yup.number().when("isdynamicInstallment", {
      is: 1,
      then: yup
        .number()
        .required("Maximum Installments required")
        .min(
          yup.ref("minimumTerm"),
          "Max must be greater than or equal to min",
        ),
      otherwise: yup.number().nullable(true),
    }),
    interestPaymentMethodId: yup
      .string()
      .required("Interest Payment Method selection required"),
    repaymentFrequencyId: yup
      .string()
      .required("Payment Interval selection required"),
    maxLtvPct: yup.number().when("isSecuredproduct", {
      is: (isSecuredproduct) => isSecuredproduct,
      then: yup
        .number()
        .required("LTV Ceiling required")
        .typeError("LTV Ceiling must be a valid number")
        .max(100, "LTV Ceiling must be below 100%"),
      otherwise: yup.number().nullable(true),
    }),
    lienGracePeriod: yup
      .number()
      .nullable(true)
      .typeError("Lien Grace Period must be a valid number")
      .min(0, "Lien Grace Period cannot be negative"),
    installmentPenalty: yup
      .number()
      .nullable(true)
      .typeError("Installment Penalty must be a valid number")
      .integer("Installment Penalty must be a whole number")
      .min(0, "Installment Penalty cannot be negative"),
    loanTermGracePeriod: yup
      .number()
      .nullable(true)
      .typeError("Loan Term Grace Period must be a valid number")
      .min(0, "Loan Term Grace Period cannot be negative"),
    incentiveValue: yup
      .number()
      .nullable(true)
      .typeError("Incentive Value must be a valid number")
      .when("hasIncentive", {
        is: (hasIncentive) => hasIncentive,
        then: yup
          .number()
          .required("Incentive Value required")
          .min(0, "Incentive Value cannot be negative"),
        otherwise: yup.number().nullable(true),
      })
      .test(
        "max-incentive-percentage",
        "Incentive Value must be below 100% when charged as a percentage",
        function (value) {
          const { hasIncentive, isIncentivePercentage } = this.parent;
          if (hasIncentive && isIncentivePercentage) {
            return value == null || value <= 100;
          }
          return true;
        },
      ),
    minimumLoanPaid: yup
      .number()
      .nullable(true)
      .typeError("Minimum Loan Paid must be a valid number")
      .when("hasIncentive", {
        is: (hasIncentive) => hasIncentive,
        then: yup
          .number()
          .required("Minimum Loan Paid required")
          .min(0, "Minimum Loan Paid cannot be negative")
          .max(100, "Minimum Loan Paid must be below 100%"),
        otherwise: yup.number().nullable(true),
      }),
    minimumAmount: yup.number().required("Minimum Amount required"),
    maximumAmount: yup
      .number()
      .required("Maximum Amount required")
      .moreThan(
        yup.ref("minimumAmount"),
        "Max Amount must be greater than min Amount",
      ),
    minCommission: yup
      .number()
      .nullable(true)
      .typeError("Min Commission must be a valid number")
      .min(0, "Min Commission cannot be negative")
      .max(100, "Min Commission must be at most 100%"),
    maxCommission: yup
      .number()
      .nullable(true)
      .typeError("Max Commission must be a valid number")
      .min(0, "Max Commission cannot be negative")
      .max(100, "Max Commission must be at most 100%")
      .test(
        "max-gte-min",
        "Max Commission must be greater than or equal to Min Commission",
        function (value) {
          const { minCommission } = this.parent;
          if (value == null || minCommission == null) return true;
          return value >= minCommission;
        },
      ),
  });

  const handleSubmit = async (values, { setSubmitting }) => {
      const {
        name,
        description,
        isDepositRequired,
        isGroup,
        isDepositPercentage,
        interestMethodId,
        interestPaymentMethodId,
        repaymentFrequencyId,
        minimumTerm,
        maximumTerm,
        isSecuredproduct,
        armotizeSchedule,
        isBlankDisbursement,
        hasPenalty,
        allowInstallmentRepossesion,
        hasIncentive,
        isIncentivePercentage,
      } = values;
      const noOfInstallments = parseInt(values.noOfInstallments || 0, 10);
      const productInterest = parseFloat(values.productInterest);
      const companyId = parseInt(CompanyId, 10);
      const depositRequired = isDepositRequired
        ? parseFloat(values.depositRequired)
        : null;
      const minimumAmount = parseInt(values.minimumAmount);
      const maximumAmount = parseInt(values.maximumAmount);
      const isDynamicInstallmentValue = isDynamicInstallment ? 1 : 0;
      const maxLtvPct = isSecuredproduct ? parseFloat(values.maxLtvPct) : null;
      const lienGracePeriod =
        (hasPenalty || allowInstallmentRepossesion) &&
        values.lienGracePeriod !== ""
          ? parseInt(values.lienGracePeriod, 10)
          : null;
      const installmentPenalty =
        hasPenalty && values.installmentPenalty !== ""
          ? parseInt(values.installmentPenalty, 10)
          : null;
      const loanTermGracePeriod =
        isSecuredproduct && values.loanTermGracePeriod !== ""
          ? parseFloat(values.loanTermGracePeriod)
          : null;
      const incentiveValue =
        hasIncentive && values.incentiveValue !== ""
          ? parseFloat(values.incentiveValue)
          : null;
      const minimumLoanPaid =
        hasIncentive && values.minimumLoanPaid !== ""
          ? parseFloat(values.minimumLoanPaid)
          : null;
      const minCommission =
        values.minCommission !== "" ? parseFloat(values.minCommission) : null;
      const maxCommission =
        values.maxCommission !== "" ? parseFloat(values.maxCommission) : null;

      const sharedVariables = {
        name,
        description,
        noOfInstallments,
        productInterest,
        isDepositRequired,
        depositRequired,
        isGroup,
        isDepositPercentage,
        minimumAmount,
        maximumAmount,
        companyId,
        interestMethodId,
        interestPaymentMethodId,
        repaymentFrequencyId,
        isdynamicInstallment: isDynamicInstallmentValue,
        minimumTerm: isDynamicInstallmentValue ? parseInt(minimumTerm) : null,
        maximumTerm: isDynamicInstallmentValue ? parseInt(maximumTerm) : null,
        isSecuredproduct,
        maxLtvPct,
        lienGracePeriod,
        installmentPenalty,
        loanTermGracePeriod,
        armotizeSchedule,
        isBlankDisbursement,
        hasPenalty,
        allowInstallmentRepossesion,
        hasIncentive,
        isIncentivePercentage,
        incentiveValue,
        minimumLoanPaid,
        minCommission,
        maxCommission,
        isDeviceCustomer: values.isDeviceCustomer,
      };

      const variables = props.isEditing
        ? { id: props.data.Id, ...sharedVariables }
        : sharedVariables;

      // ── Capture before-state for audit diff (edit mode only) ─────────────
      const beforeState = props.isEditing && props.data
        ? {
            name: props.data.Name ?? "",
            description: props.data.Description ?? "",
            noOfInstallments: props.data.NoOfInstallments ?? null,
            productInterest: props.data.ProductInterest ?? null,
            isDepositRequired: !!props.data.IsDepositRequired,
            depositRequired: props.data.DepositRequired ?? null,
            isGroup: !!props.data.IsGroup,
            isDepositPercentage: !!props.data.IsDepositPercentage,
            minimumAmount: props.data.MinimumAmount ?? null,
            maximumAmount: props.data.MaximumAmount ?? null,
            interestMethodId: props.data.InterestMethodId ?? null,
            interestPaymentMethodId: props.data.InterestPaymentMethodId ?? null,
            repaymentFrequencyId: props.data.RepaymentFrequencyId ?? null,
            isdynamicInstallment: props.data.IsdynamicInstallment ?? 0,
            minimumTerm: props.data.MinimumTerm ?? null,
            maximumTerm: props.data.maximumTerm ?? null,
            isSecuredproduct: !!props.data.IsSecuredproduct,
            maxLtvPct: props.data.MaxLtvPct ?? null,
            lienGracePeriod: props.data.LienGracePeriod ?? null,
            installmentPenalty: props.data.InstallmentPenalty ?? null,
            loanTermGracePeriod: props.data.LoanTermGracePeriod ?? null,
            armotizeSchedule: !!props.data.ArmotizeSchedule,
            isBlankDisbursement: !!props.data.IsBlankDisbursement,
            hasPenalty: !!props.data.HasPenalty,
            allowInstallmentRepossesion: !!props.data.AllowInstallmentRepossesion,
            hasIncentive: !!props.data.HasIncentive,
            isIncentivePercentage: !!props.data.IsIncentivePercentage,
            incentiveValue: props.data.IncentiveValue ?? null,
            minimumLoanPaid: props.data.MinimumLoanPaid ?? null,
            minCommission: props.data.MinCommission ?? null,
            maxCommission: props.data.MaxCommission ?? null,
            isDeviceCustomer: !!props.data.IsDeviceCustomer,
          }
        : null;

      // Current user id for audit attribution
      const auditUserId = decodedToken?.Id ?? decodedToken?.id ?? decodedToken?.UserId ?? null;

      await executeDynamicSubmit({
        mutationName: props.isEditing ? "updateProduct" : "createProduct",
        variables,
        mutationObjectName: "result",
        baseUrl: setup_url,
        isEditing: props.isEditing,
        entityName: "Product",
        onSuccess: () => {
          // ── Log audit diff after a successful update ──────────────────────
          if (props.isEditing && beforeState) {
            const afterState = { ...sharedVariables };
            // Remove internal fields that aren't meaningful for the diff
            const skipAudit = new Set(["companyId"]);
            const diff = computeFieldDiff(beforeState, afterState, skipAudit);
            if (diff.length > 0) {
              logAuditChange({
                entityType: "Product",
                recordId: props.data.Id,
                diff,
                userId: auditUserId,
                actionType: "modified",
              });
            }
          }
          props.onClose();
          props.onAction();
        },
        setSubmitting,
      });
  };

  const isLastStep = activeStep === STEPS.length - 1;

  return (
    <Box m="20px">
      <Header
        title={props.isEditing ? "Edit Product" : "Create Product"}
        subtitle={props.isEditing ? "Edit Product" : "Create Product"}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          setFieldTouched,
          validateForm,
          submitForm,
          isSubmitting,
        }) => {
          const goNext = async () => {
            const validationErrors = await validateForm();
            const stepFields = getStepFields(activeStep, values);
            const hasErrors = stepFields.some((name) => validationErrors[name]);
            if (hasErrors) {
              stepFields.forEach((name) => setFieldTouched(name, true, true));
              return;
            }
            setActiveStep((step) => step + 1);
          };

          return (
            <form
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                // Belt-and-braces: text/select fields shouldn't submit the
                // form on Enter. Buttons are untouched so Space/Enter still
                // activate Back/Next/Submit normally — actual submission is
                // only ever triggered by the Submit button's onClick below,
                // never by the browser's native implicit-submit behavior
                // (there is no type="submit" element in this form).
                if (
                  e.key === "Enter" &&
                  (e.target.tagName === "INPUT" ||
                    e.target.tagName === "SELECT")
                ) {
                  e.preventDefault();
                }
              }}
            >
              <Stepper activeStep={activeStep} sx={{ mb: 3 }} alternativeLabel>
                {STEPS.map((step) => (
                  <Step key={step.label}>
                    <StepLabel>{step.label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {STEPS[activeStep].description}
              </Typography>

              {/* ── STEP 0: Basic Details ── */}
              {activeStep === 0 && (
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                >
                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Product Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.name}
                    name="name"
                    error={touched.name && !!errors.name}
                    helperText={touched.name && errors.name}
                    InputProps={infoAdornment("Enter the name of the product")}
                  />

                  <TextField
                    fullWidth
                    variant="filled"
                    type="text"
                    label="Description"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.description}
                    name="description"
                    error={touched.description && !!errors.description}
                    helperText={touched.description && errors.description}
                    InputProps={infoAdornment(
                      "Provide a description of the product",
                    )}
                  />

                  <NumericFormat
                    customInput={TextField}
                    variant="filled"
                    fullWidth
                    name="productInterest"
                    label="Product Interest (%)"
                    value={values.productInterest ?? ""}
                    onValueChange={(vals) =>
                      setFieldValue("productInterest", vals.floatValue ?? "")
                    }
                    onBlur={handleBlur}
                    suffix="%"
                    decimalScale={2}
                    allowNegative={false}
                    isAllowed={(vals) =>
                      vals.floatValue === undefined || vals.floatValue <= 100
                    }
                    error={touched.productInterest && !!errors.productInterest}
                    helperText={
                      touched.productInterest && errors.productInterest
                    }
                    {...numericFieldProps("Provide the product interest rate")}
                  />

                  <FormControl fullWidth variant="filled">
                    <InputLabel>Interest Method</InputLabel>
                    <Select
                      name="interestMethodId"
                      value={values.interestMethodId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.interestMethodId && !!errors.interestMethodId
                      }
                    >
                      {interestMethods.map((method) => (
                        <MenuItem key={method.Id} value={method.Id}>
                          {method.Method}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.interestMethodId && errors.interestMethodId && (
                      <FormHelperText error>
                        {errors.interestMethodId}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth variant="filled">
                    <InputLabel>Interest Payment Method</InputLabel>
                    <Select
                      name="interestPaymentMethodId"
                      value={values.interestPaymentMethodId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.interestPaymentMethodId &&
                        !!errors.interestPaymentMethodId
                      }
                    >
                      {interestPaymentMethods.map((method) => (
                        <MenuItem key={method.Id} value={method.Id}>
                          {method.PaymentMethod}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.interestPaymentMethodId &&
                      errors.interestPaymentMethodId && (
                        <FormHelperText error>
                          {errors.interestPaymentMethodId}
                        </FormHelperText>
                      )}
                  </FormControl>

                  <FormControl fullWidth variant="filled">
                    <InputLabel>Payment Interval</InputLabel>
                    <Select
                      name="repaymentFrequencyId"
                      value={values.repaymentFrequencyId}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.repaymentFrequencyId &&
                        !!errors.repaymentFrequencyId
                      }
                    >
                      {repaymentFrequencys.map((method) => (
                        <MenuItem key={method.Id} value={method.Id}>
                          {method.Name}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.repaymentFrequencyId &&
                      errors.repaymentFrequencyId && (
                        <FormHelperText error>
                          {errors.repaymentFrequencyId}
                        </FormHelperText>
                      )}
                  </FormControl>

                  {/* Min / Max Commission */}
                  {isAssetFinanceEnabled && (
                  <NumericFormat
                    customInput={TextField}
                    variant="filled"
                    fullWidth
                    name="minCommission"
                    label="Min Commission (%)"
                    value={values.minCommission ?? ""}
                    onValueChange={(vals) =>
                      setFieldValue("minCommission", vals.floatValue ?? "")
                    }
                    onBlur={handleBlur}
                    suffix="%"
                    decimalScale={2}
                    allowNegative={false}
                    isAllowed={(vals) =>
                      vals.floatValue === undefined || vals.floatValue <= 100
                    }
                    error={touched.minCommission && !!errors.minCommission}
                    helperText={touched.minCommission && errors.minCommission}
                    {...numericFieldProps(
                      "Minimum commission percentage allowed for this product",
                    )}
                  />
                  )}

                  {isAssetFinanceEnabled && (
                  <NumericFormat
                    customInput={TextField}
                    variant="filled"
                    fullWidth
                    name="maxCommission"
                    label="Max Commission (%)"
                    value={values.maxCommission ?? ""}
                    onValueChange={(vals) =>
                      setFieldValue("maxCommission", vals.floatValue ?? "")
                    }
                    onBlur={handleBlur}
                    suffix="%"
                    decimalScale={2}
                    allowNegative={false}
                    isAllowed={(vals) =>
                      vals.floatValue === undefined || vals.floatValue <= 100
                    }
                    error={touched.maxCommission && !!errors.maxCommission}
                    helperText={touched.maxCommission && errors.maxCommission}
                    {...numericFieldProps(
                      "Maximum commission percentage allowed for this product",
                    )}
                  />
                  )}
                </Box>
              )}

              {/* ── STEP 1: Installments & Amounts ── */}
              {activeStep === 1 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  <Box>
                    <CheckboxField
                      checked={isDynamicInstallment === 1}
                      onChange={(e) => {
                        setIsDynamicInstallment(e.target.checked ? 1 : 0);
                        setFieldValue(
                          "isdynamicInstallment",
                          e.target.checked ? 1 : 0,
                        );
                      }}
                      name="isdynamicInstallment"
                      label="Is Dynamic Installment"
                      tooltip="Allow the loan officer to choose the number of installments within a min/max range, instead of a fixed count"
                    />
                    <Box
                      display="grid"
                      gap="20px"
                      gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                      sx={{ mt: 1 }}
                    >
                      {isDynamicInstallment === 0 ? (
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label="No Of Installments"
                          name="noOfInstallments"
                          value={values.noOfInstallments || 0}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "noOfInstallments",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          decimalScale={0}
                          allowNegative={false}
                          error={
                            touched.noOfInstallments &&
                            !!errors.noOfInstallments
                          }
                          helperText={
                            touched.noOfInstallments && errors.noOfInstallments
                          }
                        />
                      ) : (
                        <>
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Minimum Installments"
                            name="minimumTerm"
                            value={values.minimumTerm || ""}
                            onValueChange={(vals) =>
                              setFieldValue(
                                "minimumTerm",
                                vals.floatValue ?? "",
                              )
                            }
                            onBlur={handleBlur}
                            decimalScale={0}
                            allowNegative={false}
                            error={touched.minimumTerm && !!errors.minimumTerm}
                            helperText={
                              touched.minimumTerm && errors.minimumTerm
                            }
                          />
                          <NumericFormat
                            customInput={TextField}
                            fullWidth
                            variant="filled"
                            label="Maximum Installments"
                            name="maximumTerm"
                            value={values.maximumTerm || ""}
                            onValueChange={(vals) =>
                              setFieldValue(
                                "maximumTerm",
                                vals.floatValue ?? "",
                              )
                            }
                            onBlur={handleBlur}
                            decimalScale={0}
                            allowNegative={false}
                            error={touched.maximumTerm && !!errors.maximumTerm}
                            helperText={
                              touched.maximumTerm && errors.maximumTerm
                            }
                          />
                        </>
                      )}
                    </Box>
                  </Box>

                  <Box
                    display="grid"
                    gap="20px"
                    gridTemplateColumns={{ xs: "1fr", sm: "repeat(2, 1fr)" }}
                  >
                    <NumericFormat
                      customInput={TextField}
                      fullWidth
                      variant="filled"
                      label="Minimum Loan Amount"
                      name="minimumAmount"
                      value={values.minimumAmount ?? ""}
                      onValueChange={(vals) =>
                        setFieldValue("minimumAmount", vals.floatValue ?? "")
                      }
                      onBlur={handleBlur}
                      thousandSeparator
                      decimalScale={2}
                      allowNegative={false}
                      error={touched.minimumAmount && !!errors.minimumAmount}
                      helperText={touched.minimumAmount && errors.minimumAmount}
                      {...numericFieldProps(
                        "Enter the minimum amount for the product range",
                      )}
                    />

                    <NumericFormat
                      customInput={TextField}
                      fullWidth
                      variant="filled"
                      label="Maximum Loan Amount"
                      name="maximumAmount"
                      value={values.maximumAmount ?? ""}
                      onValueChange={(vals) =>
                        setFieldValue("maximumAmount", vals.floatValue ?? "")
                      }
                      onBlur={handleBlur}
                      thousandSeparator
                      decimalScale={2}
                      allowNegative={false}
                      error={touched.maximumAmount && !!errors.maximumAmount}
                      helperText={touched.maximumAmount && errors.maximumAmount}
                      {...numericFieldProps(
                        "Enter the maximum amount for the product range",
                      )}
                    />
                  </Box>

                  <Box>
                    <CheckboxField
                      checked={values.isDepositRequired}
                      onChange={(e) =>
                        setFieldValue("isDepositRequired", e.target.checked)
                      }
                      name="isDepositRequired"
                      label="Is Deposit Required"
                      tooltip="Check if a deposit is required for this product"
                    />

                    {values.isDepositRequired && (
                      <Box
                        display="grid"
                        gap="20px"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                        }}
                        sx={{ mt: 1 }}
                        alignItems="center"
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label={`Deposit Required${values.isDepositPercentage ? " (%)" : " (KES)"}`}
                          name="depositRequired"
                          value={values.depositRequired ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "depositRequired",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          thousandSeparator={!values.isDepositPercentage}
                          suffix={values.isDepositPercentage ? "%" : undefined}
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(vals) =>
                            !values.isDepositPercentage ||
                            vals.floatValue === undefined ||
                            vals.floatValue <= 100
                          }
                          error={
                            touched.depositRequired && !!errors.depositRequired
                          }
                          helperText={
                            touched.depositRequired && errors.depositRequired
                          }
                        />
                        <CheckboxField
                          checked={values.isDepositPercentage}
                          onChange={(e) =>
                            setFieldValue(
                              "isDepositPercentage",
                              e.target.checked,
                            )
                          }
                          name="isDepositPercentage"
                          label="Deposit as Percentage"
                          tooltip="Check if the deposit amount is a percentage of the total amount"
                        />
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* ── STEP 2: Collateral & Penalties ── */}
              {activeStep === 2 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  {(isAssetFinanceEnabled || isDeviceEnabled) && (
                  <Box>
                    {isAssetFinanceEnabled && (
                      <CheckboxField
                        checked={values.isSecuredproduct}
                        onChange={(e) =>
                          setFieldValue("isSecuredproduct", e.target.checked)
                        }
                        name="isSecuredproduct"
                        label="Is Secured Loan"
                        tooltip="Check if this product requires collateral security"
                      />
                    )}
                    {isDeviceEnabled && (
                      <CheckboxField
                        checked={values.isDeviceCustomer}
                        onChange={(e) =>
                          setFieldValue("isDeviceCustomer", e.target.checked)
                        }
                        name="isDeviceCustomer"
                        label="Is Product Device"
                        tooltip="Check if this product is for a Device"
                      />
                    )}

                    {isAssetFinanceEnabled && values.isSecuredproduct && (
                      <Box
                        display="grid"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                        }}
                        gap={2}
                        sx={{ mt: 1 }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label="LTV Ceiling (%)"
                          name="maxLtvPct"
                          value={values.maxLtvPct ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue("maxLtvPct", vals.floatValue ?? "")
                          }
                          onBlur={handleBlur}
                          suffix="%"
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(vals) =>
                            vals.floatValue === undefined ||
                            vals.floatValue <= 100
                          }
                          error={touched.maxLtvPct && !!errors.maxLtvPct}
                          helperText={touched.maxLtvPct && errors.maxLtvPct}
                          {...numericFieldProps(
                            "Maximum loan-to-value ratio allowed for this secured product",
                          )}
                        />
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label="Loan Term Grace Period (Days)"
                          name="loanTermGracePeriod"
                          value={values.loanTermGracePeriod ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "loanTermGracePeriod",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          decimalScale={0}
                          allowNegative={false}
                          error={
                            touched.loanTermGracePeriod &&
                            !!errors.loanTermGracePeriod
                          }
                          helperText={
                            touched.loanTermGracePeriod &&
                            errors.loanTermGracePeriod
                          }
                          {...numericFieldProps(
                            "Grace period, in days, before loan term penalties apply",
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                  )}

                  <Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexWrap="wrap"
                      gap={2}
                    >
                      <CheckboxField
                        checked={values.hasPenalty}
                        onChange={(e) =>
                          setFieldValue("hasPenalty", e.target.checked)
                        }
                        name="hasPenalty"
                        label="Has Penalty"
                        tooltip="Charge a penalty on installments that go past the grace period"
                      />
                      {isAssetFinanceEnabled && (
                        <CheckboxField
                          checked={values.allowInstallmentRepossesion}
                          onChange={(e) =>
                            setFieldValue(
                              "allowInstallmentRepossesion",
                              e.target.checked,
                            )
                          }
                          name="allowInstallmentRepossesion"
                          label="Allow Installment Repossession"
                          tooltip="Repossess collateral once an installment breaches the grace period, instead of only charging a penalty"
                        />
                      )}
                    </Box>

                    {(values.hasPenalty ||
                      (isAssetFinanceEnabled &&
                        values.allowInstallmentRepossesion)) && (
                      <Box
                        display="grid"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                        }}
                        gap={2}
                        sx={{ mt: 1 }}
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label="Installment Grace Period (Days)"
                          name="lienGracePeriod"
                          value={values.lienGracePeriod ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "lienGracePeriod",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          decimalScale={0}
                          allowNegative={false}
                          error={
                            touched.lienGracePeriod && !!errors.lienGracePeriod
                          }
                          helperText={
                            touched.lienGracePeriod && errors.lienGracePeriod
                          }
                          {...numericFieldProps(
                            "Number of days an overdue installment is allowed before it triggers a penalty and/or repossession",
                          )}
                        />
                        {values.hasPenalty && (
                          // <NumericFormat
                          //   customInput={TextField}
                          //   fullWidth
                          //   variant="filled"
                          //   label="Installment Penalty (KES)"
                          //   name="installmentPenalty"
                          //   value={values.installmentPenalty ?? ""}
                          //   onValueChange={(vals) =>
                          //     setFieldValue(
                          //       "installmentPenalty",
                          //       vals.floatValue ?? "",
                          //     )
                          //   }
                          //   onBlur={handleBlur}
                          //   thousandSeparator
                          //   decimalScale={0}
                          //   allowNegative={false}
                          //   error={
                          //     touched.installmentPenalty &&
                          //     !!errors.installmentPenalty
                          //   }
                          //   helperText={
                          //     touched.installmentPenalty &&
                          //     errors.installmentPenalty
                          //   }
                          //   {...numericFieldProps(
                          //     "Penalty amount charged per missed installment",
                          //   )}
                          // />
                          <NumericFormat
                              customInput={TextField}
                              fullWidth
                              variant="filled"
                              label="Installment Penalty (%)"
                              name="installmentPenalty"
                              value={values.installmentPenalty ?? ""}
                              onValueChange={(vals) =>
                                setFieldValue(
                                  "installmentPenalty",
                                  vals.floatValue ?? "",
                                )
                              }
                              onBlur={handleBlur}
                              suffix="%"
                              decimalScale={2}
                              allowNegative={false}
                              isAllowed={(vals) =>
                                vals.floatValue === undefined ||
                                vals.floatValue <= 100
                              }
                              error={
                                touched.installmentPenalty &&
                                !!errors.installmentPenalty
                              }
                              helperText={
                                touched.installmentPenalty &&
                                errors.installmentPenalty
                              }
                              {...numericFieldProps(
                                "Percentage penalty charged for each missed installment",
                              )}
                            />
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {/* ── STEP 3: Incentives & Extras ── */}
              {activeStep === 3 && (
                <Box display="flex" flexDirection="column" gap={3}>
                  {isAssetFinanceEnabled && (
                  <Box>
                    <CheckboxField
                      checked={values.hasIncentive}
                      onChange={(e) =>
                        setFieldValue("hasIncentive", e.target.checked)
                      }
                      name="hasIncentive"
                      label="Has Incentive"
                      tooltip="Allow an interest waiver / incentive to be applied to loans on this product"
                    />

                    {values.hasIncentive && (
                      <Box
                        display="grid"
                        gridTemplateColumns={{
                          xs: "1fr",
                          sm: "repeat(2, 1fr)",
                        }}
                        gap={2}
                        sx={{ mt: 1 }}
                        alignItems="center"
                      >
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label={`Incentive Value${values.isIncentivePercentage ? " (%)" : " (KES)"}`}
                          name="incentiveValue"
                          value={values.incentiveValue ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "incentiveValue",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          thousandSeparator={!values.isIncentivePercentage}
                          suffix={
                            values.isIncentivePercentage ? "%" : undefined
                          }
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(vals) =>
                            !values.isIncentivePercentage ||
                            vals.floatValue === undefined ||
                            vals.floatValue <= 100
                          }
                          error={
                            touched.incentiveValue && !!errors.incentiveValue
                          }
                          helperText={
                            touched.incentiveValue && errors.incentiveValue
                          }
                        />
                        <CheckboxField
                          checked={values.isIncentivePercentage}
                          onChange={(e) =>
                            setFieldValue(
                              "isIncentivePercentage",
                              e.target.checked,
                            )
                          }
                          name="isIncentivePercentage"
                          label="Incentive as Percentage"
                          tooltip="Check if the incentive is a percentage of interest rather than a flat KES amount"
                        />
                        <NumericFormat
                          customInput={TextField}
                          fullWidth
                          variant="filled"
                          label="Minimum interest to be paid (%)"
                          name="minimumLoanPaid"
                          value={values.minimumLoanPaid ?? ""}
                          onValueChange={(vals) =>
                            setFieldValue(
                              "minimumLoanPaid",
                              vals.floatValue ?? "",
                            )
                          }
                          onBlur={handleBlur}
                          suffix="%"
                          decimalScale={2}
                          allowNegative={false}
                          isAllowed={(vals) =>
                            vals.floatValue === undefined ||
                            vals.floatValue <= 100
                          }
                          error={
                            touched.minimumLoanPaid && !!errors.minimumLoanPaid
                          }
                          helperText={
                            touched.minimumLoanPaid && errors.minimumLoanPaid
                          }
                          {...numericFieldProps(
                            "Minimum percentage of interest that the customer must pay",
                          )}
                        />
                      </Box>
                    )}
                  </Box>
                  )}

                  <Box display="flex" flexDirection="column" gap={1}>
                    <CheckboxField
                      checked={values.isGroup}
                      onChange={(e) =>
                        setFieldValue("isGroup", e.target.checked)
                      }
                      name="isGroup"
                      label="Is Group"
                      tooltip="Check if this product is for a group"
                    />
                    <CheckboxField
                      checked={values.armotizeSchedule}
                      onChange={(e) =>
                        setFieldValue("armotizeSchedule", e.target.checked)
                      }
                      name="armotizeSchedule"
                      label="Amortize Schedule"
                      tooltip="Check to generate an amortized (reducing balance) repayment schedule for this product"
                    />
                    <CheckboxField
                      checked={values.isBlankDisbursement}
                      onChange={(e) =>
                        setFieldValue("isBlankDisbursement", e.target.checked)
                      }
                      name="isBlankDisbursement"
                      label="Is Blank Disbursement"
                      tooltip="Check if loans on this product are disbursed without a predefined amount"
                    />
                    
                  </Box>
                </Box>
              )}

              <Box display="flex" justifyContent="space-between" mt="30px">
                <Button
                  type="button"
                  variant="outlined"
                  disabled={activeStep === 0}
                  onClick={() => setActiveStep((step) => step - 1)}
                >
                  Back
                </Button>
                {isLastStep ? (
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    disabled={isSubmitting}
                    onClick={submitForm}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                ) : (
                  <Button
                    type="button"
                    color="primary"
                    variant="contained"
                    onClick={goNext}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </form>
          );
        }}
      </Formik>
    </Box>
  );
};

export default Form;